import * as Sentry from '@sentry/react';

/**
 * Setup global fetch interceptor to capture HTTP errors in Sentry
 * This will send 4xx and 5xx errors to Sentry with full context
 */
export function setupFetchInterceptor() {
  // Save the original fetch function
  const originalFetch = window.fetch;

  // Override the global fetch
  window.fetch = async function (...args) {
    const [url, options = {}] = args;
    const method = options.method || 'GET';
    const startTime = Date.now();

    try {
      // Call the original fetch
      const response = await originalFetch(...args);
      const duration = Date.now() - startTime;

      // Check if the response status indicates an error
      if (!response.ok) {
        const status = response.status;
        const statusText = response.statusText;

        // Clone the response to read the body (response can only be read once)
        let responseData;
        try {
          const clonedResponse = response.clone();
          const contentType = clonedResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            responseData = await clonedResponse.json();
          } else {
            responseData = await clonedResponse.text();
          }
        } catch (e) {
          responseData = 'Unable to read response body';
        }

        // Create an error object
        const error = new Error(`HTTP ${status}: ${method} ${url} - ${statusText}`);
        error.name = 'FetchError';

        // Capture the error in Sentry for 4xx and 5xx status codes
        if (status >= 400) {
          Sentry.captureException(error, {
            level: status >= 500 ? 'error' : 'warning',
            tags: {
              http_status: status,
              http_method: method.toUpperCase(),
              request_url: url.toString(),
            },
            extra: {
              status_code: status,
              status_text: statusText,
              response_data: responseData,
              request_duration_ms: duration,
              request_config: {
                method,
                url: url.toString(),
                headers: options.headers,
                body: options.body,
              },
            },
            fingerprint: [
              'fetch-error',
              String(status),
              method.toUpperCase(),
              url.toString(),
            ],
          });

          console.error(`[Fetch Error ${status}] ${method.toUpperCase()} ${url}`, {
            status,
            statusText,
            responseData,
            duration: `${duration}ms`,
          });
        }
      }

      return response;
    } catch (error) {
      // Network error, timeout, or other fetch errors
      const duration = Date.now() - startTime;

      Sentry.captureException(error, {
        level: 'error',
        tags: {
          error_type: 'network_error',
          http_method: method.toUpperCase(),
          request_url: url.toString(),
        },
        extra: {
          error_message: error.message,
          request_duration_ms: duration,
          request_config: {
            method,
            url: url.toString(),
            headers: options.headers,
            body: options.body,
          },
        },
        fingerprint: [
          'fetch-network-error',
          url.toString(),
        ],
      });

      console.error('[Fetch Network Error]', error.message, {
        method,
        url: url.toString(),
        duration: `${duration}ms`,
      });

      // Re-throw the error so the calling code can handle it
      throw error;
    }
  };

  console.log('[Fetch Interceptor] Global fetch interceptor installed for Sentry error tracking');
}

